import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Phonebook, PhonebookAccess, PhonebookCompany, PhonebookCompanyInput, PhonebookContact,
  PhonebookContactInput,
} from '@arcnet/contracts';
import { api } from './client.js';

/**
 * The phonebook (docs/32): the client's own contacts and companies. The API answers only the
 * account's own users; for anybody else these queries fail and the screens are not offered.
 */

export const phonebookKeys = {
  all: (t: string) => ['phonebook', t] as const,
  access: (t: string) => ['phonebook', t, 'access'] as const,
  companies: (t: string, q: string) => ['phonebook', t, 'companies', q] as const,
  contacts: (t: string, f: ContactFilter) => ['phonebook', t, 'contacts', f] as const,
};

export interface ContactFilter {
  book: Phonebook;
  department_id?: string | undefined;
  extension_id?: string | undefined;
  company_id?: string | undefined;
  q?: string | undefined;
  limit?: number | undefined;
}

const query = (f: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const usePhonebookAccess = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: phonebookKeys.access(tenantId),
    queryFn: () => api.get<PhonebookAccess>(`/v1/tenants/${tenantId}/phonebook`),
    enabled: enabled && !!tenantId,
  });

export const useCompanies = (tenantId: string, q = '') =>
  useQuery({
    queryKey: phonebookKeys.companies(tenantId, q),
    queryFn: () => api.get<{ data: PhonebookCompany[] }>(`/v1/tenants/${tenantId}/phonebook/companies${query({ q })}`),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
  });

export const useContacts = (tenantId: string, f: ContactFilter, enabled = true) =>
  useQuery({
    queryKey: phonebookKeys.contacts(tenantId, f),
    queryFn: () => api.get<{ data: PhonebookContact[]; total: number }>(
      `/v1/tenants/${tenantId}/phonebook/contacts${query({ ...f })}`),
    enabled: enabled && !!tenantId,
    placeholderData: keepPreviousData,
  });

/** Every change touches lists the others show (a contact's company, a company's count). */
const invalidate = (qc: ReturnType<typeof useQueryClient>, tenantId: string) =>
  qc.invalidateQueries({ queryKey: phonebookKeys.all(tenantId) });

export function useSaveCompany(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PhonebookCompanyInput & { id?: string }) => (id
      ? api.put<{ id: string }>(`/v1/tenants/${tenantId}/phonebook/companies/${id}`, body)
      : api.post<{ id: string }>(`/v1/tenants/${tenantId}/phonebook/companies`, body)),
    onSuccess: () => invalidate(qc, tenantId),
  });
}

export function useDeleteCompany(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/phonebook/companies/${id}`),
    onSuccess: () => invalidate(qc, tenantId),
  });
}

export function useSaveContact(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PhonebookContactInput & { id?: string }) => (id
      ? api.put<PhonebookContact>(`/v1/tenants/${tenantId}/phonebook/contacts/${id}`, body)
      : api.post<PhonebookContact>(`/v1/tenants/${tenantId}/phonebook/contacts`, body)),
    onSuccess: () => invalidate(qc, tenantId),
  });
}

export function useDeleteContact(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/phonebook/contacts/${id}`),
    onSuccess: () => invalidate(qc, tenantId),
  });
}
